import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  LifebuoyIcon,
} from '@heroicons/react/24/outline';
import SharedBadge from '@/global/Badge/Badge';
import SharedButton from '@/global/Button/Button';
import './activation-error.css';

type ActivationErrorStatus = 'BrokenLink' | 'ExpiredCode' | 'ResentCode' | '';

const SUPPORT_EMAIL = 'support@disgenet.com';

const STATUS_CONTENT: Record<
  Exclude<ActivationErrorStatus, ''>,
  {
    badge: string;
    title: string;
    intro: string;
    causes: string[];
    icon: 'error' | 'info';
  }
> = {
  BrokenLink: {
    badge: 'Broken link',
    title: 'Something went wrong with your link',
    intro:
      'We could not activate your account with this confirmation link. The link may be incomplete or no longer valid.',
    causes: [
      'The link was copied incompletely or broke across lines in the email.',
      'The activation link has already been used.',
      'The confirmation code is no longer valid.',
      'A temporary issue occurred while processing the request.',
    ],
    icon: 'error',
  },
  ExpiredCode: {
    badge: 'Expired link',
    title: 'Your confirmation link has expired',
    intro:
      'Activation links are time-limited for security. This one is no longer valid, so we could not complete the activation from it.',
    causes: [
      'Too much time passed before opening the email link.',
      'An older confirmation email was used instead of the latest one.',
      'The link was already used or revoked.',
    ],
    icon: 'error',
  },
  ResentCode: {
    badge: 'New link sent',
    title: 'Your confirmation link has expired',
    intro:
      'We sent a new activation email to your inbox. Please use the most recent message to activate your account.',
    causes: [
      'The previous confirmation link expired.',
      'A new activation email may take a few minutes to arrive.',
      'Check spam or promotions folders if you do not see it.',
    ],
    icon: 'info',
  },
};

const DEFAULT_CONTENT = {
  badge: 'Activation error',
  title: 'We could not activate your account',
  intro: 'There has been an error while confirming your account. This can happen for a few common reasons.',
  causes: [
    'The activation link is incomplete, expired, or already used.',
    'The confirmation code is no longer valid.',
    'A temporary issue occurred while processing the request.',
    'Your account may already be activated.',
  ],
  icon: 'error' as const,
};

function getStatusFromUrl(): ActivationErrorStatus {
  const status = new URLSearchParams(window.location.search).get('status') ?? '';
  if (status === 'BrokenLink' || status === 'ExpiredCode' || status === 'ResentCode') {
    return status;
  }
  return '';
}

const ActivationErrorContent = () => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const urlStatus = useMemo(() => getStatusFromUrl(), []);
  const content = urlStatus ? STATUS_CONTENT[urlStatus] : DEFAULT_CONTENT;

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="activation-error-page">
      <div className="activation-error-page__shell">
        <header className="activation-error-page__hero">
          <span
            className={`activation-error-page__icon${
              content.icon === 'info' ? ' activation-error-page__icon--info' : ''
            }`}
            aria-hidden
          >
            {content.icon === 'info' ? <EnvelopeIcon /> : <ExclamationTriangleIcon />}
          </span>
          <SharedBadge
            variant={content.icon === 'info' ? 'primaryLight' : 'error'}
            size="xs"
            className="activation-error-page__badge mb-0"
            text={content.badge}
          />
          <h1 className="activation-error-page__title">{content.title}</h1>
          <p className="activation-error-page__intro">{content.intro}</p>
        </header>

        <aside className="activation-error-page__login-tip" role="note">
          <span className="activation-error-page__login-tip-icon" aria-hidden>
            <InformationCircleIcon />
          </span>
          <div className="activation-error-page__login-tip-copy">
            <h2 className="activation-error-page__login-tip-title">Try logging in</h2>
            <p className="activation-error-page__login-tip-body">
              Activation may have completed successfully even if this page shows an error. Sign in with your
              credentials to check whether your account is already active.
            </p>
          </div>
        </aside>

        <section className="activation-error-page__panel" aria-labelledby="activation-error-causes">
          <h2 id="activation-error-causes" className="activation-error-page__panel-title">
            Possible causes
          </h2>
          <ul className="activation-error-page__causes">
            {content.causes.map(cause => (
              <li key={cause}>{cause}</li>
            ))}
          </ul>
        </section>

        <div className="activation-error-page__ctas">
          <SharedButton
            type="button"
            variant="primary"
            size="sm"
            weight="xl"
            arrow="right"
            iconLeft={<ArrowRightOnRectangleIcon className="w-4 h-4" aria-hidden />}
            onClick={() => navigate('/login')}
          >
            Go to login
          </SharedButton>
          <SharedButton
            type="button"
            variant="primaryOutline"
            size="sm"
            weight="xl"
            iconLeft={<LifebuoyIcon className="w-4 h-4" aria-hidden />}
            onClick={() => navigate('/Support')}
          >
            Contact support
          </SharedButton>
        </div>

        <p className="activation-error-page__support">
          Still stuck? Email us at{' '}
          <button
            type="button"
            className="activation-error-page__copy"
            title="Copy email"
            onClick={copyEmail}
          >
            {SUPPORT_EMAIL}
          </button>
          {copied ? <span className="activation-error-page__copied">copied!</span> : null}
        </p>
      </div>
    </section>
  );
};

export default ActivationErrorContent;
